import React from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { Box, Paper, Typography, Button, CircularProgress } from '@mui/material';
import { Lock, AlertTriangle } from 'react-feather';
import { usePayment } from '../../contexts/PaymentContext';

/**
 * Componente que verifica o bloqueio de pagamento e redireciona se necessario
 * - Bloqueio parcial (15-30 dias ou sem assinatura): So permite Inicio, Config, Suporte
 * - Bloqueio total (30+ dias): Redireciona para tela de bloqueio
 */
const PaymentGuard = ({ children }) => {
  const location = useLocation();
  const {
    status,
    statusData,
    loading,
    isPartiallyBlocked,
    isBlocked,
    isRouteAllowed,
    openPaymentModal,
    STATUS
  } = usePayment();

  // Mostrar loading enquanto verifica status
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Bloqueio total - nao deveria chegar aqui (tratado no login)
  // Mas por seguranca, mostra mensagem de bloqueio
  if (isBlocked()) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '80vh',
          p: 3
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 5,
            maxWidth: 500,
            textAlign: 'center',
            borderTop: '4px solid #b71c1c'
          }}
        >
          <Lock
            size={64}
            style={{
              color: '#b71c1c',
              marginBottom: 16
            }}
          />
          <Typography variant="h4" gutterBottom color="error">
            Acesso Bloqueado
          </Typography>
          <Typography variant="body1" color="textSecondary" paragraph>
            {statusData?.mensagem || 'Seu acesso foi bloqueado por falta de pagamento.'}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Entre em contato com o suporte para regularizar sua situacao.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Bloqueio parcial - verificar se a rota atual e permitida
  if (isPartiallyBlocked() && !isRouteAllowed(location.pathname)) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '80vh',
          p: 3
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 5,
            maxWidth: 550,
            textAlign: 'center',
            borderTop: '4px solid #d32f2f'
          }}
        >
          <AlertTriangle
            size={64}
            style={{
              color: '#d32f2f',
              marginBottom: 16
            }}
          />
          <Typography variant="h4" gutterBottom color="error">
            Acesso Limitado
          </Typography>
          <Typography variant="body1" color="textSecondary" paragraph>
            {status === STATUS.SEM_ASSINATURA
              ? 'Voce ainda nao possui um plano ativo.'
              : 'Devido ao atraso no pagamento, seu acesso esta limitado.'}
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            Voce pode acessar apenas as seguintes areas:
          </Typography>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="primary">
              - Inicio / Dashboard
            </Typography>
            <Typography variant="body2" color="primary">
              - Configuracoes
            </Typography>
            <Typography variant="body2" color="primary">
              - Suporte
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="error"
            onClick={() => openPaymentModal()}
            sx={{ mr: 2 }}
          >
            Regularizar Pagamento
          </Button>
          <Button
            variant="outlined"
            color="primary"
            href="/inicio/dashboard"
          >
            Ir para Inicio
          </Button>
        </Paper>
      </Box>
    );
  }

  // Acesso permitido - renderizar conteudo
  return <>{children}</>;
};

PaymentGuard.propTypes = {
  children: PropTypes.node.isRequired,
};

export default PaymentGuard;
